
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import { ComponentDocs, Preview } from "@lighting-beetle/lighter-styleguide";
import Grid, { Container, GridCol } from "./index";

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};

const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Grid`}</h1>
    <p>{`Grid has mobile first design and consists of 12 columns across 5 different media breakpoints XS (default) / S / M / L / XL.`}</p>
    <h2>{`Example`}</h2>
    <Grid mdxType="Grid">
  <GridCol mdxType="GridCol">
    <div className="l-debug">default</div>
  </GridCol>
  <GridCol size={4} mdxType="GridCol">
    <div className="l-debug">4</div>
  </GridCol>
  <GridCol size="shrink" mdxType="GridCol">
    <div className="l-debug">shrink</div>
  </GridCol>
  <GridCol size="auto" mdxType="GridCol">
    <div className="l-debug">fill</div>
  </GridCol>
    </Grid>
    <h2>{`Variants`}</h2>
    <h3>{`Column sizes`}</h3>
    <p>{`Start by creating a wrapper with a class of `}<inlineCode parentName="p">{`<Grid />`}</inlineCode>{`. Then add some `}<inlineCode parentName="p">{`<GridCol />`}</inlineCode>{` elements. A column takes all space available by default, but shrinks to accomodate other columns as well in one row, unless they are so wide they won't fit in.`}</p>
    <p>{`To define a column size number 1-12 or one of `}<inlineCode parentName="p">{`[shrink/fill/auto]`}</inlineCode>{` can be used.`}</p>
    <ul>
      <li parentName="ul">{`1-12 fixed width`}</li>
      <li parentName="ul">{`shrink column takes the least amount of space available`}</li>
      <li parentName="ul">{`fill column takes all the space available and does not shrink to accomodate other columns`}</li>
      <li parentName="ul">{`auto behaves the same as `}<inlineCode parentName="li">{`<GridCol />`}</inlineCode>{`. this class is needed for responsive behavior explanied later on`}</li>
    </ul>
    <Preview code={<Container mdxType="Container">
      <Grid mdxType="Grid">
        <GridCol mdxType="GridCol">...</GridCol>
        <GridCol size={4} mdxType="GridCol">...</GridCol>
        <GridCol size="shrink" mdxType="GridCol">...</GridCol>
        <GridCol size="auto" mdxType="GridCol">...</GridCol>
      </Grid>
    </Container>} mdxType="Preview">
  <Container mdxType="Container">
    <Grid mdxType="Grid">
      <GridCol mdxType="GridCol">
        <div className="l-debug">default</div>
      </GridCol>
      <GridCol size={4} mdxType="GridCol">
        <div className="l-debug">4</div>
      </GridCol>
      <GridCol size="shrink" mdxType="GridCol">
        <div className="l-debug">shrink</div>
      </GridCol>
      <GridCol size="auto" mdxType="GridCol">
        <div className="l-debug">fill</div>
      </GridCol>
    </Grid>
  </Container>
    </Preview>
    <h2>{`Responsive behavior`}</h2>
    <p>{`Breakpoints are one of `}{`[XS (default) / S / M / L / XL]`}{`. There is no need to specify the `}<inlineCode parentName="p">{`xs`}</inlineCode>{` breakpoint, because it's default by definition of mobile first philosophy.`}</p>
    <p>{`Basic column behavior gets applied on any element with class containing `}<inlineCode parentName="p">{`<GridCol />`}</inlineCode>{`. Therefore a `}<inlineCode parentName="p">{`<GridCol size={{ m: 5 }} />`}</inlineCode>{` will automatically behave as `}<inlineCode parentName="p">{`size={12}`}</inlineCode>{` on viewports smaller than M.`}</p>
    <Preview code={<Container mdxType="Container">
      <Grid mdxType="Grid">
        <GridCol size={{
          xs: 6,
          m: 4
        }} mdxType="GridCol" />
        <GridCol size={{
          xs: 4,
          m: "fill"
        }} mdxType="GridCol" />
        <GridCol size={{
          xs: 12,
          l: "shrink"
        }} mdxType="GridCol" />
      </Grid>
    </Container>} mdxType="Preview">
  <Container mdxType="Container">
    <Grid mdxType="Grid">
      <GridCol size={{
            xs: 6,
            m: 4
          }} mdxType="GridCol">
        <div className="l-debug">6, m-4</div>
      </GridCol>
      <GridCol size={{
            xs: 4,
            m: "fill"
          }} mdxType="GridCol">
        <div className="l-debug">4, m-fill</div>
      </GridCol>
      <GridCol size={{
            xs: 12,
            l: "shrink"
          }} mdxType="GridCol">
        <div className="l-debug">12, l-shrink</div>
      </GridCol>
    </Grid>
  </Container>
    </Preview>
    <h3>{`Offsets`}</h3>
    <Preview mdxType="Preview">
  <Container mdxType="Container">
    <Grid mdxType="Grid">
      <GridCol size={6} mdxType="GridCol">
        <div className="l-debug">6</div>
      </GridCol>
      <GridCol size={4} offset={2} mdxType="GridCol">
        <div className="l-debug">4, Offset 2</div>
      </GridCol>
      <GridCol size={6} offset="center" mdxType="GridCol">
        <div className="l-debug">6, Offset center</div>
      </GridCol>
    </Grid>
  </Container>
    </Preview>
    <h2>{`Container`}</h2>
    <p>{`A `}<inlineCode parentName="p">{`<Container />`}</inlineCode>{` element applies padding and max width and keeps the grid centered.`}</p>
    <Preview code={<Container mdxType="Container">
      <Grid mdxType="Grid" />
    </Container>} mdxType="Preview">
  <Container mdxType="Container">
    <Grid mdxType="Grid">
      <GridCol mdxType="GridCol">
        <div className="l-debug">default</div>
      </GridCol>
      <GridCol size={4} mdxType="GridCol">
        <div className="l-debug">4</div>
      </GridCol>
      <GridCol size="shrink" mdxType="GridCol">
        <div className="l-debug">shrink</div>
      </GridCol>
      <GridCol size="auto" mdxType="GridCol">
        <div className="l-debug">fill</div>
      </GridCol>
    </Grid>
  </Container>
    </Preview>
    <h2>{`Props`}</h2>
    <ComponentDocs title="<Grid />" component={Grid} mdxType="ComponentDocs" />
    <ComponentDocs title="<GridCol />" component={GridCol} mdxType="ComponentDocs" />
    <ComponentDocs title="<Container />" component={Container} mdxType="ComponentDocs" />
    <h2>{`Accessibility`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`<Grid />`}</inlineCode>{` and `}<inlineCode parentName="p">{`<GridCols />`}</inlineCode>{` components are purely presentational and do not carry any semantic meaning.`}</p>
    <p>{`When using `}<inlineCode parentName="p">{`<Grid />`}</inlineCode>{` and `}<inlineCode parentName="p">{`<GridCols />`}</inlineCode>{` for listing items (products, articles, etc...), it is recommended to take advantage of the `}<inlineCode parentName="p">{`tag`}</inlineCode>{` property and make into an unordered list:`}</p>
    <Preview mdxType="Preview">
  <Grid tag="ul" className="list--unstyled" mdxType="Grid">
    <GridCol tag="li" size={6} mdxType="GridCol">
      item 1
    </GridCol>
    <GridCol tag="li" size={6} mdxType="GridCol">
      item 2
    </GridCol>
    <GridCol tag="li" size={6} mdxType="GridCol">
      item 3
    </GridCol>
    <GridCol tag="li" size={6} mdxType="GridCol">
      item 4
    </GridCol>
  </Grid>
    </Preview>
`;
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;